import { atom } from 'nanostores';
import type { ComputedStylePayload, ElementDebugSource } from '../preview-bridge/protocol';
import type { HotEditListContext } from '../preview-bridge/listContext';
import type { HotEditElementKind } from '../preview-bridge/elementKind';
import type { HotEditPropSection } from '../preview-bridge/elementKind';

export type HotEditBridgeApi = {
  applyInstantCss: (elementId: string, property: string, value: string) => void;
  applyInstantText: (elementId: string, text: string) => void;
  /** Debounced RAF-like instant text preview for inputs that re-render rapidly */
  scheduleApplyInstantText: (elementId: string, text: string) => void;
  applyInstantDom: (elementId: string, key: string, value: string) => void;
  bumpMergedFromTailwind: (property: string, value: string) => void;
  queuePersistTypographyLayout: () => void;
  queuePersistText: (text: string) => void;
  queuePersistPlaceholder: (placeholder: string) => void;
  queuePersistDomValue: (value: string) => void;
  queuePersistSelectOptions: (options: string[]) => void;
  queuePersistInputType: (type: string) => void;
  openSelectionInEditor: () => void;
  exitHotEditMode: () => void;
};

export type HotEditSelection = {
  elementId: string;
  tagName: string;
  ownerComponentName?: string;
  source?: ElementDebugSource | null;
  implementationSource?: ElementDebugSource | null;
  /** DOM path segments for nth-of-type / debugging */
  selectorPath?: string;
  /** Repeated list instance (.map loops) — from preview fiber + DOM */
  listContext?: HotEditListContext | null;
  computedStyles: ComputedStylePayload;
  textContent: string;
  className: string;
  hasChildElements: boolean;
  elementKind?: HotEditElementKind;
  editTargetKind?: 'callSite' | 'designSystemOnly' | 'missing';
  canPersistClasses?: boolean;
  blockReason?: string | null;
  inputPlaceholder?: string;
  inputValue?: string;
  inputType?: string;
  optionValues?: string[];
  /** Derived on host — sections rendered for this selection */
  editableSections?: HotEditPropSection[];
};

/** Claude-style inspect + Tweaks drawer mode */
export const hotEditStore = {
  mode: atom<boolean>(false),
  selection: atom<HotEditSelection | null>(null),
  iframePathname: atom<string>('/'),
  /** Wired by {@link packages/workbench/components/Workbench} when iframe bridge is mounted */
  bridgeApi: atom<HotEditBridgeApi | null>(null),
};
